import React, { useEffect, useMemo, useState } from "react";
import "../../Assets/Styles/components/templateListSlider.scss";

import Fade from "@mui/material/Fade";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { useDispatch, useSelector } from "react-redux";
import deleteTemplate from "../../Assets/Images/deleteTemplate.svg";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import {
  DeleteTemplateByID,
  DeleteAllTemplates,
  GetTemplateData,
  RenameTemplate,
} from "../../actions/Template/Template";
import { clearBatchUrls, setImageUrl, setLoader } from "../../Redux/MainImage/imageSlice";
import { setEdit_Template, setID, setTemplateAuthoringAspectPreset } from "../../Redux/UserSlice/userSlice";
import { useTranslation } from "react-i18next";
import { setWaterMarksLoader } from "../../Redux/WaterMarks/watermarks";
import { RootState } from "../../Redux/store";
import { TEMPLATE_DEMO_BACKGROUND_URL } from "../../Config/templateAspectRatios";
import { setExtension } from "../../Redux/DownloadSlice/downloadSlice";
import { useHistory } from "react-router-dom";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import PremiumGateDialog from "../Common/PremiumGateDialog";
import Loader from "../Common/Loader";
import helper_functions from "../../Config/helper_functions";
interface TemplateItem {
  id: string;
  name: string;
  date?: number | string;
  media?: { thumbUrl?: string; originalUrl?: string; url?: string; largeUrl?: string } | null;
  thumbUrl?: string;
  thumbnailUrl?: string;
  originalUrl?: string;
  url?: string;
}

const TemplateListSlider = () => {
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | "all" | null>(null);
  const [deleteSnackbarOpen, setDeleteSnackbarOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [gateDialogOpen, setGateDialogOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [templateToRename, setTemplateToRename] = useState<TemplateItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameSnackbarOpen, setRenameSnackbarOpen] = useState(false);
  // Per-thumbnail load tracking, keyed by template id -- each thumbnail shows a skeleton
  // until its own <img> fires onLoad, instead of popping in mid-load/broken.
  const [loadedThumbs, setLoadedThumbs] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  // Client-side only -- there's no created/updated timestamp on TemplateItem to sort by, so
  // this just reverses the order the API already returned them in.
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const memoizedTemplates = useMemo(() => {
    return sortOrder === "oldest" ? [...templates].reverse() : templates;
  }, [templates, sortOrder]);
  const dispatch = useDispatch();
  const history = useHistory();
  const isPremium = usePremiumStatus();
  useEffect(() => {
    const t = userSliceState.template as unknown;
    setTemplates(Array.isArray(t) ? (t as TemplateItem[]) : []);
  }, [userSliceState.template]);
  useEffect(() => {
    if (selected) {
      const fetchDataAndAskForImage = async () => {
        dispatch(setLoader(true));
        dispatch(setEdit_Template(true));
        dispatch(setID(selected));
        const result = await dispatch(
          GetTemplateData(
            selected,
            [],
            false,
            true,
            userSliceState.canvasSize?.width ?? 651,
            isPremium === true ? true : false
          )
        );
        console.log("result76", result);
        if (!result) return;
        // Ask for an example image before `/edit`, or user can use "Use sample background"
        // in the modal (same demo asset as new-template flow).
      };
      fetchDataAndAskForImage();
    }
  }, [selected]);

  const handleDeleteModal = (id: string) => {
    setDeleteModal(true);
    setIdToDelete(id);
  };

  const handleUseSampleBackground = () => {
    dispatch(setExtension("png"));
    dispatch(
      setImageUrl({
        url: TEMPLATE_DEMO_BACKGROUND_URL,
        name: "canvasBgDemo.png",
        metadata: JSON.stringify({}),
      })
    );
    dispatch(setTemplateAuthoringAspectPreset("1:1"));
    // Editing a template with the demo background is a single-image session. `mainImage` isn't
    // persisted but survives SPA navigation, so a stale `batchurls` from an earlier batch
    // session would send `initializeMainImage` down its batch branch and the demo background
    // would never render (canvas stays behind the "Downloading Template" overlay).
    dispatch(clearBatchUrls());
    history.push("/edit");
  };

  useEffect(() => {
    if (selected) {
      handleUseSampleBackground();
    }
  }, [selected]);

  return (
    <>
      <div className="template-section-header">
        <span className="template-section-header__title">{t("myTemplates")}</span>
        <div className="template-section-header__controls">
          <div className="template-view-toggle" role="group" aria-label="Layout">
            <button
              type="button"
              className={`template-view-toggle__btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
                <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
            <button
              type="button"
              className={`template-view-toggle__btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <select
            className="template-sort-select"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
      </div>

      <div className={`template-grid template-grid--${viewMode}`} role="list">
        {/* Free/Pro status (usePremiumStatus) isn't resolved yet -- rendering the grid
            now would show every template unlocked, then flip some to locked once the
            check settles. Show skeleton placeholders instead until it's known. */}
        {isPremium === null
          ? Array.from({ length: memoizedTemplates.length || 4 }).map((_, i) => (
              <div key={i} className="template-card">
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={150}
                  sx={{ borderRadius: "0.5rem" }}
                />
                <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
              </div>
            ))
          : memoizedTemplates.length > 0 &&
            memoizedTemplates.map((template, index) => {
              const locked = !isPremium && index > 0;
              const thumbLoaded = Boolean(loadedThumbs[template.id]);
              const handleSelect = () => {
                if (locked) {
                  setGateDialogOpen(true);
                  return;
                }
                dispatch(setWaterMarksLoader(true));
                setSelected(template.id);
              };
              return (
                <div key={template.id} className="template-card">
                  {/* Image with src="template.media.originalUrl" - wrapper enforces uniform height */}
                  <div
                    className={`templateThumbnailWrapper rounded ${
                      selected === template.id
                        ? "borderTempaltesSelected"
                        : "borderTempaltes"
                    } position-relative`}
                    style={{ backgroundColor: "#f5f5f5" }}
                    onClick={handleSelect}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleSelect();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {locked && (
                      <span className="st-card__pro-badge">Pro</span>
                    )}
                    {/* Skeleton shows until this specific thumbnail's onLoad fires, so a
                        slow-loading image doesn't show as a blank/broken box. */}
                    {!thumbLoaded && (
                      <Skeleton
                        variant="rounded"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", inset: 0 }}
                      />
                    )}
                    <img
                      className={`templateThumbnail ${locked ? "locked-template-slider-thumbnail" : ""}`}
                      src={
                        template?.media?.thumbUrl ||
                        template?.media?.url ||
                        template?.media?.originalUrl ||
                        template?.thumbUrl ||
                        template?.thumbnailUrl ||
                        ""
                      }
                      alt={template?.name || "Template"}
                      style={thumbLoaded ? undefined : { visibility: "hidden" }}
                      onLoad={() =>
                        setLoadedThumbs((prev) => ({ ...prev, [template.id]: true }))
                      }
                      onError={() =>
                        setLoadedThumbs((prev) => ({ ...prev, [template.id]: true }))
                      }
                    />
                    {locked && (
                      <div className="template-lock-overlay">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="white"
                          className="template-lock-icon"
                          aria-hidden="true"
                        >
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Name + edit/delete actions, side by side below the thumbnail */}
                  <div className="template-card__footer">
                    <div className="template-card__meta">
                      <span className="template-card__name">{template.name}</span>
                      {template.date ? (
                        <span className="template-card__date">
                          {helper_functions.unixTimestampToDateTime(Number(template.date))}
                        </span>
                      ) : null}
                    </div>
                    {!locked && (
                      <div className="template-card__actions">
                        <button
                          type="button"
                          className="template-card__edit"
                          aria-label="Rename template"
                          title="Rename template"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTemplateToRename(template);
                            setRenameValue(template.name || "");
                            setRenameModalOpen(true);
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9"/>
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="template-card__delete"
                          aria-label="Delete template"
                          title="Delete template"
                          onClick={() => handleDeleteModal(template.id)}
                        >
                          <img alt="" src={deleteTemplate} width={16} height={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
      </div>
      <Modal
        open={renameModalOpen}
        onClose={() => {
          setRenameModalOpen(false);
          setTemplateToRename(null);
        }}
        closeAfterTransition
      >
        <Fade in={renameModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
            }}
            className="p-4 bg-white rounded-4"
          >
            <h4 className="deleteTempateHeading aktiv text-center text-black mb-3">
              Rename Template
            </h4>
            <div className="mb-3">
              <label className="form-label text-muted text_14 aktiv">Template Name</label>
              <input
                type="text"
                className="form-control aktiv text_15"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Enter template name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && renameValue.trim() && !renameLoading) {
                    e.preventDefault();
                    const saveBtn = document.getElementById("confirm-rename-btn");
                    saveBtn?.click();
                  }
                }}
              />
            </div>
            <div className="d-flex justify-content-center mt-4">
              <button
                type="button"
                className="button_text text-light m-0 w_8_rem py-1 mx-2 bgDisabled border-0 d-flex justify-content-center align-items-center button_text_signin"
                disabled={renameLoading}
                onClick={() => {
                  setRenameModalOpen(false);
                  setTemplateToRename(null);
                }}
              >
                Cancel
              </button>
              <button
                id="confirm-rename-btn"
                type="button"
                className="button_text text-light m-0 w_8_rem py-1 mx-2 bgCreate border-0 d-flex justify-content-center align-items-center button_text_signin"
                disabled={renameLoading || !renameValue.trim()}
                onClick={async () => {
                  if (!templateToRename || !renameValue.trim()) return;
                  setRenameLoading(true);
                  try {
                    const success = await dispatch(
                      RenameTemplate(templateToRename.id, renameValue.trim())
                    );
                    if (success) {
                      setRenameModalOpen(false);
                      setTemplateToRename(null);
                      setRenameSnackbarOpen(true);
                    }
                  } finally {
                    setRenameLoading(false);
                  }
                }}
              >
                {renameLoading ? <Loader size={16} /> : "Save"}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
      <Snackbar
        open={renameSnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setRenameSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRenameSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Template renamed successfully.
        </Alert>
      </Snackbar>
      <Modal
        open={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setIdToDelete(null);
        }}
        closeAfterTransition
      >
        <Fade in={deleteModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
            }}
            className="p-4 bg-white rounded-4"
          >
            <h4 className="deleteTempateHeading aktiv text-center text-black">
              {idToDelete === "all"
                ? "Delete All Templates?"
                : "Delete Template?"}
            </h4>
            <p className="text-center text-black aktiv">
              {idToDelete === "all"
                ? "You are about to delete all templates permanently."
                : "You are about to delete this template permanently."}
              <br />
              This can&apos;t be undone.
            </p>
            <div className="d-flex justify-content-center mt-4">
              <button
                className="button_text text-light m-0 w_8_rem py-1 mx-2 bgDisabled border-0 d-flex justify-content-center align-items-center button_text_signin"
                disabled={deleteLoading}
                onClick={() => {
                  setDeleteModal(false);
                  setIdToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`button_text text-light m-0 w_8_rem py-1 mx-2 bgCreate border-0 d-flex justify-content-center align-items-center button_text_signin`}
                disabled={deleteLoading}
                onClick={async () => {
                  setDeleteLoading(true);
                  try {
                    if (idToDelete === "all") {
                      await dispatch(DeleteAllTemplates());
                    } else if (idToDelete) {
                      // Needed: show user feedback only after single-delete request completes.
                      await dispatch(DeleteTemplateByID(idToDelete));
                      setDeleteSnackbarOpen(true);
                    }
                    setDeleteModal(false);
                    setIdToDelete(null);
                  } finally {
                    setDeleteLoading(false);
                  }
                }}
              >
                {deleteLoading ? (
                  <Loader size={16} />
                ) : idToDelete === "all" ? (
                  "Delete All"
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
      <Snackbar
        open={deleteSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setDeleteSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDeleteSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Template deleted successfully.
        </Alert>
      </Snackbar>
      <PremiumGateDialog
        open={gateDialogOpen}
        onClose={() => setGateDialogOpen(false)}
        title="Upgrade to Pro"
        message="Free users can only access the first template. Upgrade to Pro to unlock all your saved templates."
      />
    </>
  );
};

export default TemplateListSlider;
